import {
  useMutation,
  UseMutationResult,
  useQuery,
  UseQueryResult,
} from "react-query";

// Props untuk useMutateApi
type UseMutateApiProps<TData, TVariables> = {
  key: unknown;
  fetch: (variables: TVariables) => Promise<TData>;
  retry?: boolean | number;
  onSuccess?: (data: TData, variables: TVariables) => void;
  onError?: (error: unknown) => void;
  allResponse?: boolean;
};

// Hook untuk mutation
export const useMutateApi = <TData = any, TVariables = void>({
  key,
  fetch,
  retry = false,
  onSuccess,
  onError,
  allResponse = false,
}: UseMutateApiProps<TData, TVariables>) => {
  const mutation: UseMutationResult<TData, unknown, TVariables> = useMutation({
    mutationKey: [key], 
    mutationFn: fetch,
    retry,
    onSuccess: (res, variables) =>
      onSuccess && onSuccess(allResponse ? res : (res as any)?.data, variables),
    onError,
  });

  return {
    response: mutation.data,
    data: (mutation.data as any)?.data,
    isLoading: mutation.isLoading,
    mutate: mutation.mutate,
    mutateAsync: mutation.mutateAsync,
    error: mutation.error,
    isError: mutation.isError,
  };
};

// Props untuk useGetApi
type UseGetApiProps<TData> = {
  key: unknown;
  fetch: () => Promise<TData>;
  enabled?: boolean;
  retry?: boolean | number;
  refetchOnWindowFocus?: boolean;
  keepPreviousData?: boolean;
  onSuccess?: (data: TData) => void;
  onError?: (error: unknown) => void;
  allResponse?: boolean;
};

export const useGetApi = <TData = any>({
  key,
  fetch,
  enabled = true,
  retry = false,
  refetchOnWindowFocus = false,
  keepPreviousData = true,
  onSuccess,
  onError,
  allResponse = false,
}: UseGetApiProps<TData>) => {
  const query: UseQueryResult<TData> = useQuery({
    queryKey: [key], 
    queryFn: fetch,
    enabled,
    retry,
    refetchOnWindowFocus,
    keepPreviousData,
    onSuccess: (res) =>
      onSuccess && onSuccess(allResponse ? res : (res as any)?.data),
    onError,
  });

  return {
    response: query.data,
    data: (query.data as any)?.data,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    refetch: query.refetch,
    isError: query.isError,
    error: query.error,
  };
};
